import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { type UserResponse } from "@/api/auth";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import { listCollections } from "@/api/memories";
import {
  CollectionCard,
  MemoriesSearch,
  StoryThemesCard,
} from "@/components/memories";

const Home = () => {
  const { data } = useSuspenseQuery<UserResponse>(meQuery);
  const firstName = data.name?.split(" ")[0] ?? "there";

  return (
    <AppShell user={data}>
      {/* Greeting */}
      <section className="mb-8">
        <p className="font-mono text-[13px] uppercase tracking-[0.18em] text-taupe-deep">
          Welcome back
        </p>
        <h1
          className="mt-1 font-serif text-[40px] leading-[44px] text-ink"
          style={{ fontWeight: 600 }}
        >
          Hello,{" "}
          <em className="text-blush" style={{ fontStyle: "italic" }}>
            {firstName}
          </em>
          .
        </h1>
      </section>

      {/* Your memories — the iOS Memilies hub: search + category cards */}
      <YourMemories circles={data.families.length} />
    </AppShell>
  );
};

const YourMemories = ({ circles }: { circles: number }) => {
  const [searchActive, setSearchActive] = useState(false);
  const collections = useQuery({
    queryKey: ["media-collections"] as const,
    queryFn: listCollections,
    staleTime: 60_000,
  });

  // null ⇒ browse surface not mounted for this env; error ⇒ transient.
  // Either way the hub keeps the quiet empty state — never a dead grid.
  const summaries = collections.data ?? null;
  const hasBrowse = !!summaries && summaries.length > 0;

  return (
    <section aria-label="Your memories">
      <div className="mb-4 flex items-baseline justify-between">
        <h2
          className="font-serif text-[22px] uppercase text-ink"
          style={{ fontWeight: 600, letterSpacing: "0.06em" }}
        >
          Your memories
        </h2>
        <span className="font-mono text-[13px] uppercase tracking-[0.18em] text-taupe-deep">
          {circles} circle{circles === 1 ? "" : "s"}
        </span>
      </div>

      <MemoriesSearch onActiveChange={setSearchActive} />

      {!searchActive && (
        <div className="mt-5">
          {hasBrowse ? (
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {summaries.map((s) => (
                <CollectionCard key={s.collection_key} summary={s} />
              ))}
              <StoryThemesCard />
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-sand bg-white/60 p-10 text-center">
              <p className="mx-auto max-w-sm font-serif text-[20px] italic leading-[28px] text-ink-soft">
                "Saving a memory is not nostalgia. It is provision."
              </p>
              <p className="mt-6 font-sans text-[14px] text-ink-soft">
                {collections.data === null
                  ? "Nothing kept yet. Bring in a photo from Facebook or Google Photos to start."
                  : "Search your shared moments, or browse People, Trips, and Time Travel as they fill in."}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export const Route = createFileRoute("/home")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: Home,
});
